import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, IconButton, Modal, Typography, Menu, MenuItem } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, PieLabelRenderProps, Label } from 'recharts';
import CloseIcon from '@mui/icons-material/Close';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';
import OpenInFullIcon from '../../../../assets/icons/Expand.svg';
import EditIcon from '../../../../assets/icons/edit.svg';
import CollapseIcon from '../../../../assets/icons/CollapseIcon.svg';
import RestartAltIcon from '../../../../assets/icons/Reset.svg';
import CommonAnalysisTable from './CommonAnalysisTable';
import EditDataModal from './EditDataModal';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';
import { borderColor } from '@mui/system';

interface ChartPieData {
  title?: string;
  data?: any;
  containerClassName?: string;
  centerLabel?: string;
  infoIcon?: React.ReactNode;
  tooltipMessage?: string;
  selectionBox?: boolean;
  getDataForSelection?: (selectedType: string | null) => any;
  getTitleForSelection?: (selectedType: string | null) => string;
  tableTitle?: string;
  tableHeaderText?: string;
  tableColumns?: Array<{ key: string; label: string; align?: 'left' | 'center' | 'right' }>;
  tableData?: any[];
  onSelectionChange?: (selectedType: string | null) => void;
  onTableIconClick?: (selectedAlert: string | null) => void;
  paginationInfo?: any;
  onPaginationChange?: (cursor: any) => void;
  onCardDataUpdate?: (chartId?: number, startDate?: string, endDate?: string) => void;
  chartId?: number;
  useFleetTableStyle?: boolean;
}

const CommonPieChart: React.FC<ChartPieData> = ({
  title: baseTitle,
  data: initialData,
  containerClassName,
  centerLabel,
  infoIcon,
  tooltipMessage,
  selectionBox = true,
  getDataForSelection,
  getTitleForSelection,
  tableTitle = 'Analysis Table',
  tableHeaderText = 'Detailed information',
  tableColumns = [],
  tableData = [],
  useFleetTableStyle = false,
  onSelectionChange,
  onTableIconClick,
  paginationInfo,
  onPaginationChange,
  onCardDataUpdate,
  chartId,
}) => {
  const theme = useTheme();
  const { showToast } = useToast();
  const [containerSize, setContainerSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [selectedAlert, setSelectedAlert] = useState<string | null>(null);
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [isExpandedModalOpen, setIsExpandedModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [resetTrigger, setResetTrigger] = useState<number>(0);
  const open = Boolean(anchorEl);

  // Get data based on selection if function is provided
  const data = getDataForSelection ? getDataForSelection(selectedAlert) : initialData;

  // Get dynamic title if function is provided
  const title = getTitleForSelection ? getTitleForSelection(selectedAlert) : baseTitle;

  useEffect(() => {
    const handleResize = () => {
      setContainerSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Maintain consistent colors
  const alertColorMap: { [key: string]: string } = {};
  data?.alertNameWithValueList?.forEach((alert: { alertName: string }, index: number) => {
    alertColorMap[alert.alertName] = data.coloursName[index] || 'grey';
  });

  // If getDataForSelection is provided, don't filter again (already filtered)
  // Otherwise, filter based on selectedAlert
  const chartPieValue = data
    ? data.alertNameWithValueList
        .filter((alert: { alertName: string }) =>
          getDataForSelection ? true : selectedAlert ? alert.alertName === selectedAlert : true,
        )
        .map((alert: { alertName: string; alertValue: number }) => ({
          warningName: alert.alertName,
          colorCode: alertColorMap[alert.alertName],
          value: alert.alertValue,
        }))
    : [];

  const totalAlertValue = chartPieValue.reduce((sum: any, alert: { value: any }) => sum + alert.value, 0);

  const renderCustomizedLabel = (props: PieLabelRenderProps) => {
    let { cx, cy } = props;
    if (cx === undefined || cy === undefined) {
      cx = 150;
      cy = 150;
    }
    return (
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" style={{ fill: '#000' }}>
        <tspan x={cx} dy="-0.5em" fontSize="32" fontWeight="bold">
          {totalAlertValue}
        </tspan>
        <tspan x={cx} dy="1.2em" fontSize="16">
          {centerLabel}
        </tspan>
      </text>
    );
  };

  const alertColorMapping: { [key: string]: string } = chartPieValue.reduce(
    (acc: any, item: { warningName: any; colorCode: any }) => ({
      ...acc,
      [item.warningName]: item.colorCode,
    }),
    {},
  );

  const handelTable = () => {
    setIsTableOpen(!isTableOpen);
    // Call the callback function if provided
    if (onTableIconClick) {
      onTableIconClick(selectedAlert);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    console.log('Expand clicked for:', baseTitle);
    setIsExpandedModalOpen(true);
    handleMenuClose();
  };

  const handleEditData = () => {
    console.log('Edit Data clicked for:', baseTitle);
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleApplyEditData = (data: any) => {
    console.log('Applied edit data:', data);
    // Call the onCardDataUpdate callback to refresh the chart data
    if (onCardDataUpdate) {
      onCardDataUpdate(chartId, data.startDate, data.endDate);
      // Show toast message
      const startDateFormatted = new Date(data.startDate).toLocaleDateString('en-GB');
      const endDateFormatted = new Date(data.endDate).toLocaleDateString('en-GB');
      showToast(`Date range applied: ${startDateFormatted} to ${endDateFormatted}`, 'success');
    }
  };

  const handleResetChanges = () => {
    console.log('Reset Changes clicked for:', baseTitle);
    handleMenuClose();
    setIsEditModalOpen(false);
    setResetTrigger((prev) => prev + 1);
    // Reset to last 7 days
    if (onCardDataUpdate) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onCardDataUpdate(chartId, startDate, endDate);
    }
  };

  const handleCloseExpandedModal = () => {
    setIsExpandedModalOpen(false);
  };

  return (
    <Box className="w-full h-full flex flex-col border rounded-md p-4 gap-2 items-start bg-white">
      <Box className="w-full flex items-center">
        <Box className="flex items-center gap-2 w-full">
          <Typography
            variant="h6"
            fontSize={18}
            sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
          >
            {title}
          </Typography>
          {infoIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: 16,
                height: 16,
              }}
            >
              {infoIcon}
            </Box>
          )}
        </Box>
        <Box className={`flex items-center gap-2`}>
          {selectionBox && (
            <CustomDropDownInput
              options={[
                { value: '', label: 'All Alerts' },
                ...((data?.warningTypes || data?.alertNameWithValueList)?.map(
                  (alert: { alertName: string }) => ({
                    value: alert.alertName,
                    label: alert.alertName,
                  }),
                ) || []),
              ]}
              value={selectedAlert || ''}
              onChange={(value) => {
                const selectedValue = value || null;
                setSelectedAlert(selectedValue);
                // Call the callback when selection changes
                if (onSelectionChange) {
                  onSelectionChange(selectedValue);
                }
              }}
              showDropdownArrow
              customStyles={{
                width: 'auto',
                height: '40px',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                minWidth: 'auto',
                '&:hover': {
                  background: '#FFFFFF',
                },
                typography: {
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: theme.palette.primary.main,
                  whiteSpace: 'nowrap',
                },
                iconColor: theme.palette.primary.main,
              }}
            />
          )}
          <Box
            className="flex items-center cursor-pointer"
            onClick={handelTable}
            sx={{ pt: '10px', gap: 0.3 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                flexShrink: 0,
              }}
            >
              <img src={eyeIcon} alt="Table" width={24} height={24} style={{ display: 'block' }} />
            </Box>
            <Typography variant="body2" fontSize={14} sx={(theme) => ({ color: theme.palette.primary.main })}>
              Table
            </Typography>
          </Box>
          <Box className="flex items-center gap-1 cursor-pointer" sx={{ pt: '10px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                flexShrink: 0,
              }}
            >
              <IconButton
                sx={{
                  p: 0,
                  width: '24px !important',
                  height: '24px !important',
                  minWidth: '24px !important',
                  minHeight: '24px !important',
                  maxWidth: '24px !important',
                  maxHeight: '24px !important',
                  flexShrink: 0,
                  display: 'flex !important',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
                onClick={handleMenuClick}
              >
                <img
                  src={menuIcon}
                  alt="Menu"
                  width="24"
                  height="24"
                  style={{
                    display: 'block',
                    width: '24px',
                    height: '24px',
                    minWidth: '24px',
                    minHeight: '24px',
                  }}
                />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem onClick={handleExpand} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={OpenInFullIcon} alt="Expand" style={{ width: 24, height: 24 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Expand</Typography>
        </MenuItem>
        <MenuItem onClick={handleEditData} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Edit Data</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleResetChanges}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
        >
          <img
            src={RestartAltIcon}
            alt="Reset"
            style={{
              width: 20,
              height: 20,
              filter:
                'invert(34%) sepia(68%) saturate(2449%) hue-rotate(338deg) brightness(91%) contrast(84%)',
            }}
          />
          <Typography sx={{ fontSize: '14px', color: '#DC3545' }}>Reset Changes</Typography>
        </MenuItem>
      </Menu>
      {chartPieValue && chartPieValue.length > 0 && totalAlertValue > 0 ? (
        <Box className="flex sm:flex-row w-full justify-center items-center flex-col gap-4">
          <Box
            className="flex-1 flex justify-center items-center mt-8"
            sx={{
              minWidth: 0,
              minHeight: 0,
              aspectRatio: '1 / 1',
              width: '100%',
              maxWidth: '400px',
              maxHeight: '400px',
              alignSelf: 'flex-start',
            }}
          >
            <ResponsiveContainer
              key={`${containerSize.width}-${containerSize.height}`}
              width="100%"
              height="100%"
            >
              <PieChart>
                <Pie
                  data={chartPieValue}
                  dataKey="value"
                  nameKey="warningName"
                  innerRadius="65%"
                  outerRadius="85%"
                  labelLine={false}
                  startAngle={90}
                  endAngle={-270}
                >
                  {chartPieValue.map((entry: any, index: any) => (
                    <Cell key={`cell-${index}`} fill={entry.colorCode} />
                  ))}

                  <Label
                    content={({ viewBox }) => {
                      const cx = (viewBox as any)?.cx ?? 150;
                      const cy = (viewBox as any)?.cy ?? 150;

                      // Calculate font sizes based on viewBox size
                      const viewBoxWidth = (viewBox as any)?.width ?? 300;
                      const valueFontSize = Math.max(24, viewBoxWidth * 0.12);
                      const labelFontSize = Math.max(14, viewBoxWidth * 0.07);

                      return (
                        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                          <tspan x={cx} dy="-0.5em" fontSize={valueFontSize} fontWeight="bold">
                            {totalAlertValue >= 1000
                              ? `${Math.round(totalAlertValue / 1000)}K`
                              : totalAlertValue}
                          </tspan>
                          <tspan
                            x={cx}
                            dy="2em"
                            fontSize={labelFontSize}
                            fontWeight={400}
                            fill="rgba(0, 0, 0, 0.6)"
                          >
                            {centerLabel}
                          </tspan>
                        </text>
                      );
                    }}
                  />
                </Pie>

                <Tooltip
                  contentStyle={{
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Box
            className={
              baseTitle?.includes('by time bucket')
                ? 'flex flex-1 flex-col items-center flex-wrap'
                : 'flex flex-col items-center flex-1'
            }
          >
            <div
              className={
                baseTitle?.includes('by time bucket')
                  ? 'flex flex-col gap-3 flex-wrap'
                  : 'flex flex-col gap-3 flex-wrap'
              }
            >
              {chartPieValue.map((entry: any) => {
                // Fixed circle size
                const circleSize = 20;
                const borderRadius = circleSize / 2;

                return (
                  <Box
                    key={entry.warningName}
                    className="flex justify-between items-center"
                    sx={baseTitle?.includes('by time bucket') ? { gap: 2 } : { gap: '12px' }}
                  >
                    <Box className="flex items-center gap-2">
                      <Box
                        sx={{
                          width: circleSize,
                          height: circleSize,
                          backgroundColor: alertColorMapping[entry.warningName] || 'grey',
                          borderRadius: `${borderRadius}px`,
                          marginRight: 1,
                          opacity: 1,
                          transition: 'all 0.3s ease',
                          flexShrink: 0,
                          className: 'flex align-center justify-center',
                        }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight={400}
                        sx={(theme) => ({ color: theme.palette.text.secondary })}
                        noWrap
                      >
                        {entry.warningName}
                      </Typography>
                    </Box>
                    <Box
                      className={
                        baseTitle?.includes('by time bucket')
                          ? 'flex items-center gap-2 flex-shrink-0'
                          : 'flex items-center gap-2'
                      }
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={(theme) => ({ color: theme.palette.text.primary })}
                      >
                        {entry.value >= 1000 ? `${Math.round(entry.value / 1000)}K` : entry.value}
                      </Typography>
                      <Typography variant="body2" fontWeight={400} sx={(theme) => ({ color: '#767676' })}>
                        {`(${
                          totalAlertValue > 0 ? ((entry.value / totalAlertValue) * 100).toFixed(2) : '0.00'
                        }%)`}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </div>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            minHeight: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
        </Box>
      )}
      <Modal
        open={isTableOpen}
        onClose={() => setIsTableOpen(false)}
        aria-labelledby="alert-table-modal"
        aria-describedby="alert-table-description"
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            ...(useFleetTableStyle
              ? {
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  width: 'auto',
                  height: '90vh',
                  borderRadius: '8px',
                }
              : {
                  width: '90%',
                  height: '90%',
                  borderRadius: 2,
                }),
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <CommonAnalysisTable
            title={tableTitle}
            headerText={tableHeaderText}
            columns={tableColumns}
            data={tableData}
            noDataMessage="No data available for this selection"
            onClose={() => setIsTableOpen(false)}
            paginationInfo={paginationInfo}
            onPaginationChange={onPaginationChange}
          />
        </Box>
      </Modal>

      {/* Expanded Chart Modal */}
      <Modal
        open={isExpandedModalOpen}
        onClose={handleCloseExpandedModal}
        aria-labelledby="expanded-pie-chart-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
            maxWidth: { xs: 400, sm: 600, md: 900, lg: 1000 },
            maxHeight: { xs: '85vh', sm: '90vh', md: '95vh' },
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: 24,
            p: { xs: 2, sm: 2.5, md: 3 },
            outline: 'none',
            overflow: 'auto',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: { xs: '18px', sm: '24px', md: '28px' },
                  lineHeight: { xs: '27px', sm: '36px', md: '42px' },
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                }}
              >
                {title}
              </Typography>
              {infoIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}>{infoIcon}</Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Edit Chart Button */}
              <Box
                onClick={handleEditData}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  colour: '#5547DD',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={EditIcon} alt="Edit" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    color: '#5547DD',
                  }}
                >
                  Edit Chart
                </Typography>
              </Box>

              {/* Collapse Button */}
              <Box
                onClick={handleCloseExpandedModal}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={CollapseIcon} alt="Collapse" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    color: '#5547DD',
                  }}
                >
                  Collapse
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Expanded Chart Content */}
          {chartPieValue && chartPieValue.length > 0 && totalAlertValue > 0 ? (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                gap: 4,
                mt: { xs: 2, sm: 2.5, md: 3 },
                position: 'relative',
              }}
            >
              {/* Chart Section */}
              <Box
                className="flex-1 flex justify-center items-center"
                sx={{
                  minWidth: 0,
                  minHeight: 0,
                  aspectRatio: '1 / 1',
                  width: '100%',
                  maxWidth: '400px',
                  maxHeight: '400px',
                  alignSelf: 'flex-start',
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartPieValue}
                      dataKey="value"
                      nameKey="warningName"
                      innerRadius="65%"
                      outerRadius="85%"
                      labelLine={false}
                      startAngle={90}
                      endAngle={-270}
                    >
                      {chartPieValue.map((entry: any, index: any) => (
                        <Cell key={`cell-${index}`} fill={entry.colorCode} />
                      ))}

                      <Label
                        content={({ viewBox }) => {
                          const cx = (viewBox as any)?.cx ?? 150;
                          const cy = (viewBox as any)?.cy ?? 150;
                          const viewBoxWidth = (viewBox as any)?.width ?? 300;
                          const valueFontSize = Math.max(24, viewBoxWidth * 0.12);
                          const labelFontSize = Math.max(14, viewBoxWidth * 0.07);

                          return (
                            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                              <tspan x={cx} dy="-0.5em" fontSize={valueFontSize} fontWeight="bold">
                                {totalAlertValue >= 1000
                                  ? `${Math.round(totalAlertValue / 1000)}K`
                                  : totalAlertValue}
                              </tspan>
                              <tspan
                                x={cx}
                                dy="2em"
                                fontSize={labelFontSize}
                                fontWeight={400}
                                fill="rgba(0, 0, 0, 0.6)"
                              >
                                {centerLabel}
                              </tspan>
                            </text>
                          );
                        }}
                      />
                    </Pie>

                    <Tooltip
                      contentStyle={{
                        background: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              {/* Legend Section - Right side */}
              <Box
                className="flex flex-col items-center flex-1"
                sx={{
                  justifyContent: 'center',
                }}
              >
                <div className="flex flex-col gap-3 flex-wrap">
                  {chartPieValue.map((entry: any) => {
                    const circleSize = 20;
                    const borderRadius = circleSize / 2;

                    return (
                      <Box
                        key={entry.warningName}
                        className="flex justify-between items-center"
                        sx={{ gap: '12px' }}
                      >
                        <Box className="flex items-center gap-2">
                          <Box
                            sx={{
                              width: circleSize,
                              height: circleSize,
                              backgroundColor: alertColorMapping[entry.warningName] || 'grey',
                              borderRadius: `${borderRadius}px`,
                              marginRight: 1,
                              opacity: 1,
                              transition: 'all 0.3s ease',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="body2"
                            fontWeight={400}
                            sx={(theme) => ({ color: theme.palette.text.secondary })}
                            noWrap
                          >
                            {entry.warningName}
                          </Typography>
                        </Box>
                        <Box className="flex items-center gap-2">
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={(theme) => ({ color: theme.palette.text.primary })}
                          >
                            {entry.value >= 1000 ? `${Math.round(entry.value / 1000)}K` : entry.value}
                          </Typography>
                          <Typography variant="body2" fontWeight={400} sx={(theme) => ({ color: '#767676' })}>
                            {`(${
                              totalAlertValue > 0
                                ? ((entry.value / totalAlertValue) * 100).toFixed(2)
                                : '0.00'
                            }%)`}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </div>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                minHeight: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
            </Box>
          )}
        </Box>
      </Modal>

      {/* Edit Data Modal */}
      <EditDataModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onApply={handleApplyEditData}
        onReset={() => console.log('Reset clicked')}
        cardTitle={baseTitle}
        chartId={chartId}
        resetTrigger={resetTrigger}
      />
    </Box>
  );
};

export default CommonPieChart;
